/**
 * cms-client.ts
 *
 * Fetches CMS discovery page content from betty-core-api.
 * Implements sessionStorage caching (5-min TTL) and static fallback on API failure.
 * No PII — returns public CMS copy only.
 * No Sentry/error-tracking — access-landing is a public surface with no PII.
 */

const BASE_URL =
  (import.meta as unknown as { env?: Record<string, string> }).env?.['VITE_BETTY_API_BASE_URL'] ??
  '';

const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

export interface EnrollmentStep {
  readonly stepNumber: number;
  readonly title: string;
  readonly description: string;
}

export interface FaqItem {
  readonly question: string;
  readonly answer: string;
}

export interface DiscoveryPageLocaleContent {
  readonly programOverview: string;
  readonly supportedConditions: readonly string[];
  readonly benefitsList: readonly string[];
  readonly enrollmentSteps: readonly EnrollmentStep[];
  readonly faqItems: readonly FaqItem[];
  readonly contactEmail: string;
  readonly providerDirectoryUrl: string | null | undefined;
}

export interface DiscoveryPageContent {
  readonly id: string;
  readonly contentEn: DiscoveryPageLocaleContent;
  readonly contentEs: DiscoveryPageLocaleContent;
  readonly publishedAt: string;
  readonly publishedBy?: string;
}

interface CachedContent {
  data: DiscoveryPageLocaleContent;
  expiresAt: number;
}

const TRANSLATION_COMING_SOON_SUFFIX = ' (Translation coming soon)';

/**
 * Detect whether a locale content block is effectively empty (no EN translation exists yet).
 * ES content is seeded with empty strings — we treat empty programOverview as "not translated".
 */
function isEmptyLocaleContent(content: DiscoveryPageLocaleContent): boolean {
  return !content.programOverview || content.programOverview.trim().length === 0;
}

/**
 * Produce ES fallback from EN content: append "(Translation coming soon)" suffix
 * to the programOverview to signal that translation is pending.
 * Guards against undefined/null contentEn (malformed API response).
 */
function buildEsFallbackFromEn(
  en: DiscoveryPageLocaleContent | null | undefined,
): DiscoveryPageLocaleContent {
  if (!en)
    return {
      ...FALLBACK_EN_CONTENT,
      programOverview: FALLBACK_EN_CONTENT.programOverview + TRANSLATION_COMING_SOON_SUFFIX,
    };
  return {
    ...en,
    programOverview: en.programOverview + TRANSLATION_COMING_SOON_SUFFIX,
  };
}

/** Static fallback content — shown when CMS API is unavailable and no cache exists. */
export const FALLBACK_EN_CONTENT: DiscoveryPageLocaleContent = {
  programOverview:
    "NourishedRx is a fully digital nutrition program offered through the Medicare ACCESS program for people managing chronic conditions. After a doctor's referral, you're paired with Betty, an AI health companion who learns your life and helps you eat and live in a way that's right for your health — backed by registered dietitians and your own care team.",
  supportedConditions: [
    'Hypertension',
    'Prediabetes',
    'Obesity/overweight',
    'Diabetes',
    'Chronic Kidney Disease (CKD)',
    'Atherosclerotic Cardiovascular Disease (ASCVD)',
  ],
  benefitsList: [
    'Betty AI nutrition coach — available 24/7',
    'Clinical nutrition guidance from a registered dietitian',
    'Group nutrition consults',
    'Education library with recipes and meal guides',
  ],
  enrollmentSteps: [
    {
      stepNumber: 1,
      title: 'Ask your doctor',
      description:
        "Tell your doctor or care provider you'd like to join NourishedRx through Medicare.",
    },
    {
      stepNumber: 2,
      title: 'They send a referral',
      description: 'Share the link below so they can submit a quick, secure referral for you.',
    },
    {
      stepNumber: 3,
      title: 'You get an invite',
      description: "If you're eligible, we'll email and text you a link to set up and meet Betty.",
    },
  ],
  faqItems: [
    {
      question: 'Is there a cost?',
      answer: 'NourishedRx ACCESS is covered by Medicare at no cost to eligible members.',
    },
    {
      question: 'Who is eligible?',
      answer:
        'Medicare beneficiaries with a qualifying condition and a participating provider referral.',
    },
    {
      question: 'How long does enrollment take?',
      answer:
        'Once your provider submits a referral, you should receive your invitation email within 3–5 business days.',
    },
    {
      question: 'Can I refer myself?',
      answer:
        'Enrollment requires a referral from a participating healthcare provider. Ask your doctor if you are interested in joining.',
    },
  ],
  contactEmail: 'access@nourishedrx.com',
  providerDirectoryUrl: null,
};

/** Read the raw cache entry for a lang key. Returns null on miss or parse error. */
function readCache(lang: 'en' | 'es'): CachedContent | null {
  const raw = sessionStorage.getItem(`nrx_cms_discovery_${lang}`);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as CachedContent;
  } catch {
    return null;
  }
}

function getCached(lang: 'en' | 'es'): DiscoveryPageLocaleContent | null {
  const entry = readCache(lang);
  if (!entry) return null;
  return Date.now() < entry.expiresAt ? entry.data : null;
}

function getStaleCached(lang: 'en' | 'es'): DiscoveryPageLocaleContent | null {
  return readCache(lang)?.data ?? null;
}

function setCache(lang: 'en' | 'es', data: DiscoveryPageLocaleContent): void {
  const cacheKey = `nrx_cms_discovery_${lang}`;
  const cached: CachedContent = { data, expiresAt: Date.now() + CACHE_TTL_MS };
  sessionStorage.setItem(cacheKey, JSON.stringify(cached));
}

/**
 * Fetch the CMS discovery page content for the given language.
 * Returns cached content if available and fresh.
 * Falls back to stale cache → static fallback if API fails.
 */
export async function fetchDiscoveryContent(
  lang: 'en' | 'es',
): Promise<DiscoveryPageLocaleContent> {
  // 1. Check fresh cache
  const freshCached = getCached(lang);
  if (freshCached) return freshCached;

  // 2. Attempt API fetch
  try {
    const res = await fetch(`${BASE_URL}/v1/core/cms/discovery-page`);
    if (!res.ok) {
      throw new Error(`CMS fetch failed: ${String(res.status)}`);
    }
    const content = (await res.json()) as DiscoveryPageContent;

    let localeData: DiscoveryPageLocaleContent;
    if (lang === 'es') {
      // Detect empty ES content (seeded with empty strings, awaiting human translation)
      // Fall back to EN + "(Translation coming soon)" suffix rather than rendering blank sections
      localeData = isEmptyLocaleContent(content.contentEs)
        ? buildEsFallbackFromEn(content.contentEn)
        : content.contentEs;
    } else {
      localeData = content.contentEn;
    }

    setCache(lang, localeData);
    return localeData;
  } catch (error) {
    // Only log to console (no Sentry on access-landing — no PII surface)
    console.warn(
      '[cms-client] CMS fetch failed, using fallback',
      error instanceof Error ? error.message : String(error),
    );

    // 3. Return stale cache if available
    const stale = getStaleCached(lang);
    if (stale) return stale;

    // 4. Return static fallback:
    //    - EN: static FALLBACK_EN_CONTENT
    //    - ES: FALLBACK_EN_CONTENT with "(Translation coming soon)" suffix (spec requirement)
    return lang === 'es' ? buildEsFallbackFromEn(FALLBACK_EN_CONTENT) : FALLBACK_EN_CONTENT;
  }
}
